import { SettingsEntrySource } from '@/analytics/enums/settings-entry.enums'
import { SettingsModalSection, SettingsViewTrigger } from '@/analytics/enums/settings-modal.enums'
import { OrganizationRosterEntrySource } from '@/analytics/enums/organization.enums'
import type { TrackEventFn } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'

const kSettingsEntrySources = new Set<string>(Object.values(SettingsEntrySource))

/** React Router `location.state` key used when navigating to `/dashboard/settings`. */
export type SettingsLocationState = {
  settingsEntrySource?: string
}

/** Build `location.state` for a Settings navigation so the open can attribute the view. */
export function settingsLocationState(source: SettingsEntrySource): SettingsLocationState {
  return { settingsEntrySource: source }
}

/**
 * Accepts known `SettingsEntrySource` values and falls back to `Direct` for missing/invalid
 * state (typed URL, new tab, bookmark). Browser refresh keeps history.state and is not Direct.
 * Does not inspect OAuth return query params — use `resolveSettingsViewedEntrySource` for that.
 * Mirrors `resolveMatterJurisdictionEntrySource`.
 */
export function resolveSettingsEntrySource(source: string | null | undefined): SettingsEntrySource {
  if (source != null && kSettingsEntrySources.has(source)) {
    return source as SettingsEntrySource
  }
  return SettingsEntrySource.Direct
}

/**
 * Prefer explicit `location.state`. Otherwise attribute an OAuth return query
 * (`?external_storage_handoff=` / `?external_storage_error=`) as `OauthCallback`. Missing both is `Direct`.
 * The settings redirect maps that `Direct` onto `settings_url`.
 */
export function resolveSettingsViewedEntrySource(params: { stateSource: string | null | undefined; search: string }): SettingsEntrySource {
  if (params.stateSource != null && kSettingsEntrySources.has(params.stateSource)) {
    return params.stateSource as SettingsEntrySource
  }

  const query = params.search.startsWith('?') ? params.search.slice(1) : params.search
  const searchParams = new URLSearchParams(query)
  if (searchParams.has('external_storage_handoff') || searchParams.has('external_storage_error')) {
    return SettingsEntrySource.OauthCallback
  }

  return SettingsEntrySource.Direct
}

const kSettingsModalSections = new Set<string>(Object.values(SettingsModalSection))

export function isSettingsModalSection(sectionId: string): sectionId is SettingsModalSection {
  return kSettingsModalSections.has(sectionId)
}

/**
 * Attribution for a flag-on deep link into the settings modal.
 *
 * Reads the location that is still on screen. The route then replaces the URL, which drops
 * `location.state` and the OAuth query. An account route is `account_url` unless state already
 * names a source, and an organization account also carries the roster source `account_page`.
 * A settings route prefers state, then an OAuth query, then `settings_url`.
 */
export function resolveSettingsModalRedirectAttribution(params: {
  isAccountRoute: boolean
  isOrgAccount: boolean
  stateSource: string | null | undefined
  search: string
}): { settingsEntrySource: SettingsEntrySource; organizationRosterEntrySource: OrganizationRosterEntrySource | null } {
  if (params.isAccountRoute) {
    const fromState = params.stateSource != null && kSettingsEntrySources.has(params.stateSource) ? (params.stateSource as SettingsEntrySource) : null
    return {
      settingsEntrySource: fromState ?? SettingsEntrySource.AccountUrl,
      organizationRosterEntrySource: params.isOrgAccount ? OrganizationRosterEntrySource.AccountPage : null,
    }
  }

  const fromState = params.stateSource != null && kSettingsEntrySources.has(params.stateSource) ? (params.stateSource as SettingsEntrySource) : null
  if (fromState) {
    return { settingsEntrySource: fromState, organizationRosterEntrySource: null }
  }

  const fromQuery = resolveSettingsViewedEntrySource({ stateSource: null, search: params.search })
  return {
    settingsEntrySource: fromQuery === SettingsEntrySource.OauthCallback ? SettingsEntrySource.OauthCallback : SettingsEntrySource.SettingsUrl,
    organizationRosterEntrySource: null,
  }
}

/** Settings modal view. Suppresses route feature inheritance and does not set a feature value. */
export function trackSettingsModalViewed(
  track: TrackEventFn,
  input: { settingsSection: SettingsModalSection; settingsEntrySource: SettingsEntrySource; settingsViewTrigger: SettingsViewTrigger }
): void {
  track(AnalyticsEvent.SettingsModalViewed, input, { suppressFeatureInheritance: true })
}
